import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Copy, Check, Download, ArrowLeft, Terminal } from 'lucide-react';
import Seo from '../components/Seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const NAV = [
  { id: 'introducao', label: 'Introdução' },
  { id: 'autenticacao', label: 'Autenticação' },
  { id: 'referencia', label: 'Referência da API' },
  { id: 'webhooks', label: 'Webhooks' },
  { id: 'erros', label: 'Códigos de erro' },
  { id: 'rate-limits', label: 'Rate limits' },
  { id: 'sdks', label: 'SDKs' },
  { id: 'guias', label: 'Guias e tutoriais' },
];

const CodeBlock = ({ code, lang = 'bash' }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative rounded-2xl bg-[#0a0e27] overflow-hidden shadow-lg my-5">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-white/40">{lang}</span>
        <button onClick={copy} className="p-1.5 rounded-md hover:bg-white/10 text-white/70" aria-label="Copiar">
          {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
        </button>
      </div>
      <pre className="px-5 py-5 text-xs md:text-sm text-white/90 leading-relaxed overflow-x-auto">
        <code>{code}</code>
      </pre>
    </div>
  );
};

const Endpoint = ({ method, path, desc }) => {
  const colors = {
    GET: 'bg-emerald-100 text-emerald-700',
    POST: 'bg-sky-100 text-sky-700',
    PUT: 'bg-amber-100 text-amber-700',
    DELETE: 'bg-rose-100 text-rose-700',
  };
  return (
    <div className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
      <span className={`text-[11px] font-bold px-2 py-1 rounded-md w-16 text-center ${colors[method]}`}>{method}</span>
      <code className="text-sm font-mono text-[#0a0e27]">{path}</code>
      <span className="text-sm text-slate-500 ml-auto hidden sm:block">{desc}</span>
    </div>
  );
};

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-28 pt-10 first:pt-0">
    <h2 className="text-2xl md:text-3xl font-extrabold text-[#0a0e27]">{title}</h2>
    <div className="mt-4 text-[15px] text-slate-600 leading-relaxed">{children}</div>
  </section>
);

const ApiDocs = () => {
  const location = useLocation();

  // Rola até a âncora (#secao) ao entrar na página.
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace('#', ''));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <>
      <Seo
        title="Documentação da API"
        description="Referência completa da API da CoreDesk: autenticação, endpoints de mensagens, contatos e tickets, webhooks, códigos de erro, rate limits e SDKs."
        path="/desenvolvedores/docs"
      />
      <Header />

      {/* Hero */}
      <section className="bg-[#0a0e27] relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#FF5B24]/20 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/desenvolvedores" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold">
            <ArrowLeft size={16} /> Voltar para desenvolvedores
          </Link>
          <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5B24]">Documentação</div>
              <h1 className="mt-2 text-3xl md:text-5xl font-extrabold text-white">API Reference</h1>
              <p className="mt-3 text-white/70 max-w-xl">Tudo o que você precisa para integrar com a CoreDesk: endpoints, webhooks e SDKs.</p>
            </div>
            <a href="/coredesk.postman_collection.json" download className="inline-flex items-center justify-center gap-2 btn-coral rounded-full h-11 px-5 text-sm font-semibold self-start">
              <Download size={15} /> Baixar Postman Collection
            </a>
          </div>
        </div>
      </section>

      {/* Conteúdo */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-[220px_1fr] gap-10">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <nav className="sticky top-24 space-y-1">
              {NAV.map((n) => (
                <a key={n.id} href={`#${n.id}`} className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-[#0a0e27] transition-colors">
                  {n.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Corpo */}
          <div className="min-w-0 divide-y divide-slate-100">
            <Section id="introducao" title="Introdução">
              <p>A API da CoreDesk é organizada em torno de REST. Todas as requisições usam URLs previsíveis, aceitam corpos em JSON, retornam respostas em JSON e utilizam os códigos de status HTTP padrão.</p>
              <p className="mt-3">A URL base de todas as chamadas é:</p>
              <CodeBlock lang="http" code={`https://api.coredesk.com.br/v1`} />
            </Section>

            <Section id="autenticacao" title="Autenticação">
              <p>A autenticação é feita por chave de API enviada no header <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[#0a0e27] text-[13px]">Authorization</code> como Bearer token. Gere chaves separadas para ambientes de <b>teste</b> e <b>produção</b> no painel.</p>
              <CodeBlock lang="bash" code={`curl https://api.coredesk.com.br/v1/contacts \\
  -H "Authorization: Bearer $CORE_API_KEY"`} />
              <p className="text-sm text-slate-500">Nunca exponha sua chave no frontend. Mantenha-a em variáveis de ambiente no servidor.</p>
            </Section>

            <Section id="referencia" title="Referência da API">
              <p>Principais endpoints disponíveis na versão <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[13px]">v1</code>:</p>
              <div className="mt-4 rounded-2xl border border-slate-100 p-4 bg-white">
                <Endpoint method="POST" path="/messages" desc="Envia uma mensagem" />
                <Endpoint method="GET" path="/messages/:id" desc="Consulta uma mensagem" />
                <Endpoint method="GET" path="/contacts" desc="Lista contatos" />
                <Endpoint method="POST" path="/contacts" desc="Cria um contato" />
                <Endpoint method="POST" path="/tickets" desc="Abre um ticket" />
                <Endpoint method="GET" path="/tickets/:id" desc="Consulta um ticket" />
                <Endpoint method="PUT" path="/tickets/:id" desc="Atualiza um ticket" />
              </div>

              <h3 className="mt-8 text-lg font-extrabold text-[#0a0e27]">Enviar uma mensagem</h3>
              <p className="mt-2">Dispara uma mensagem por qualquer canal conectado (WhatsApp, Instagram, e-mail).</p>
              <CodeBlock lang="bash" code={`curl -X POST https://api.coredesk.com.br/v1/messages \\
  -H "Authorization: Bearer $CORE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "channel": "whatsapp",
    "to": "+5511999999999",
    "text": "Olá! Sua compra foi confirmada."
  }'`} />
              <p className="text-sm font-semibold text-slate-500">Resposta 200</p>
              <CodeBlock lang="json" code={`{
  "id": "msg_8fj2k",
  "status": "sent",
  "channel": "whatsapp",
  "to": "+5511999999999",
  "created_at": "2026-07-11T12:00:00Z"
}`} />

              <h3 className="mt-8 text-lg font-extrabold text-[#0a0e27]">Listar contatos</h3>
              <CodeBlock lang="bash" code={`curl "https://api.coredesk.com.br/v1/contacts?limit=20" \\
  -H "Authorization: Bearer $CORE_API_KEY"`} />
            </Section>

            <Section id="webhooks" title="Webhooks">
              <p>Configure uma URL de webhook no painel para receber eventos em tempo real. Enviamos um <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[13px]">POST</code> com o payload do evento e assinamos cada requisição no header <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[13px]">X-CoreDesk-Signature</code> para você validar a origem.</p>
              <p className="mt-3 font-semibold text-[#0a0e27]">Eventos disponíveis:</p>
              <ul className="mt-2 space-y-1.5">
                {['message.received', 'message.status_updated', 'conversation.started', 'ticket.created', 'ticket.updated'].map((e) => (
                  <li key={e} className="flex items-center gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF5B24]" />
                    <code className="font-mono text-[#0a0e27]">{e}</code>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm font-semibold text-slate-500">Exemplo de payload</p>
              <CodeBlock lang="json" code={`{
  "event": "message.received",
  "data": {
    "conversation_id": "cnv_2a1",
    "from": "+5511999999999",
    "channel": "whatsapp",
    "text": "Quero saber o status do meu pedido"
  },
  "sent_at": "2026-07-11T12:01:22Z"
}`} />
            </Section>

            <Section id="erros" title="Códigos de erro">
              <p>A API usa códigos de status HTTP convencionais para indicar sucesso ou falha.</p>
              <div className="mt-4 rounded-2xl border border-slate-100 overflow-hidden">
                {[
                  ['200', 'OK — requisição bem-sucedida.'],
                  ['201', 'Created — recurso criado com sucesso.'],
                  ['400', 'Bad Request — parâmetros inválidos ou faltando.'],
                  ['401', 'Unauthorized — chave de API ausente ou inválida.'],
                  ['403', 'Forbidden — sem permissão para o recurso.'],
                  ['404', 'Not Found — recurso não encontrado.'],
                  ['429', 'Too Many Requests — rate limit excedido.'],
                  ['500', 'Server Error — algo deu errado do nosso lado.'],
                ].map(([code, desc]) => (
                  <div key={code} className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0">
                    <code className="text-sm font-bold text-[#FF5B24] w-12">{code}</code>
                    <span className="text-sm text-slate-600">{desc}</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="rate-limits" title="Rate limits">
              <p>Cada chave de API tem um limite de <b>1.000 requisições por minuto</b>. Ao exceder, a API retorna <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[13px]">429</code> com os headers abaixo, indicando quando tentar novamente.</p>
              <CodeBlock lang="http" code={`X-RateLimit-Limit: 1000
X-RateLimit-Remaining: 0
X-RateLimit-Reset: 1752235200`} />
              <p className="text-sm text-slate-500">Precisa de limites maiores? Planos Enterprise têm limites ampliados sob demanda.</p>
            </Section>

            <Section id="sdks" title="SDKs">
              <p>SDKs oficiais para acelerar sua integração. Instale e comece em minutos.</p>
              <CodeBlock lang="bash" code={`# Node.js
npm install @coredesk/sdk

# Python
pip install coredesk`} />
              <CodeBlock lang="javascript" code={`import { CoreDesk } from '@coredesk/sdk';

const core = new CoreDesk({ apiKey: process.env.CORE_API_KEY });

await core.messages.send({
  channel: 'whatsapp',
  to: '+5511999999999',
  text: 'Olá! Sua compra foi confirmada.',
});`} />
            </Section>

            <Section id="guias" title="Guias e tutoriais">
              <p>Passo a passo para os casos de uso mais comuns.</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {[
                  { t: 'Enviar sua primeira mensagem', s: 'Gere a chave, escolha o canal e dispare em menos de 5 minutos.' },
                  { t: 'Receber respostas com webhooks', s: 'Configure a URL, valide a assinatura e processe eventos.' },
                  { t: 'Sincronizar contatos', s: 'Importe e mantenha sua base de contatos atualizada via API.' },
                  { t: 'Automatizar tickets', s: 'Crie e atualize tickets a partir dos seus próprios sistemas.' },
                ].map((g) => (
                  <div key={g.t} className="rounded-2xl border border-slate-100 p-5 hover:shadow-lg transition">
                    <Terminal size={18} className="text-[#FF5B24]" />
                    <h3 className="mt-3 font-extrabold text-[#0a0e27]">{g.t}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{g.s}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 rounded-3xl bg-[#FAF6F2] p-8 text-center">
                <h3 className="text-xl font-extrabold text-[#0a0e27]">Ficou com dúvida na integração?</h3>
                <p className="mt-2 text-slate-600">Nosso time técnico ajuda você a colocar tudo no ar.</p>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <Button asChild className="btn-coral rounded-full h-11 px-6 text-sm font-semibold">
                    <Link to="/contato">Falar com o time</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-full h-11 px-6 text-sm font-semibold">
                    <a href="/coredesk.postman_collection.json" download>Baixar Postman Collection</a>
                  </Button>
                </div>
              </div>
            </Section>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ApiDocs;
